import { useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, Legend,
} from "recharts";
import {
  Crosshair, Layers, Target, HelpCircle, GitCompare, TrendingUp, GitBranch, FileCode2,
} from "lucide-react";

const NR = "STATE NOT RECORDED";
const num = (v) => (typeof v === "number" && isFinite(v) ? v : null);
const isNR = (v) => v === NR || v == null;
const fmt = (v, u = "") => (num(v) == null ? null : `${v}${u}`);

const planMetrics = (p) => {
  if (!p || typeof p !== "object") return { km: null, min: null };
  const km = num(p.total_distance_km ?? p.distance_km ?? p.km ?? p.total_km);
  const s = num(p.total_duration_s ?? p.duration_s ?? p.seconds);
  const min = s != null ? Math.round(s / 60) : num(p.duration_min ?? p.minutes);
  return { km, min };
};

const Sec = ({ idx, icon: Icon, title, children }) => (
  <div className="xray-sec">
    <div className="xray-sec__h"><span className="idx">{idx}</span><Icon size={13} />{title}</div>
    {children}
  </div>
);

const KV = ({ rows }) => (
  <dl className="kv">
    {rows.map(([k, v]) => (
      <div key={k} style={{ display: "contents" }}>
        <dt>{k}</dt>
        <dd>{v == null || v === "" ? <span className="nd-inline">{NR}</span> : String(v)}</dd>
      </div>
    ))}
  </dl>
);

const chartTheme = { grid: "#1c2836", axis: "#778698" };

// RIGHT — Decision X-Ray. Chaque VortexDecisionRecord devient une visualisation compréhensible.
export default function DecisionXRay({ detail, loading, onPickDecision, challengerDiff }) {
  const [raw, setRaw] = useState(false);

  if (loading) return <Panel><div className="xray-empty" data-testid="xray-loading">Analyse de la décision…</div></Panel>;
  if (!detail) return (
    <Panel>
      <div className="xray-empty" data-testid="xray-empty">
        <Crosshair size={34} />
        <div>Sélectionne une décision, un véhicule ou un marqueur temporel.</div>
        <div style={{ fontSize: 11 }}>Le X-Ray expliquera <b>pourquoi</b> ce choix et <b>ce qu&apos;il a produit</b>.</div>
      </div>
    </Panel>
  );
  if (detail.found === false) return <Panel><div className="xray-empty" data-testid="xray-notfound">Décision introuvable.</div></Panel>;

  const d = detail.decision || {};
  const mission = detail.mission;
  const to = detail.tour_outcome;
  const toObj = typeof to === "object" ? to : null;
  const lin = detail.lineage || {};
  const plans = (mission && typeof mission === "object") ? mission.plans : null;
  const selPlanKey = (d.final_decision?.plan_type || mission?.plan_type || "").toLowerCase();

  // E — Predicted vs Actual (données réellement stockées)
  const cmp = [];
  if (num(d.planned_distance_km) != null || num(d.actual_distance_km) != null)
    cmp.push({ name: "Distance (km)", prévu: num(d.planned_distance_km), réel: num(d.actual_distance_km) });
  if (num(d.planned_duration_s) != null || num(d.actual_duration_s) != null)
    cmp.push({ name: "Durée (min)", prévu: num(d.planned_duration_s) != null ? Math.round(d.planned_duration_s / 60) : null, réel: num(d.actual_duration_s) != null ? Math.round(d.actual_duration_s / 60) : null });
  if (toObj && (num(toObj.predicted_extra_wait_min) != null || num(toObj.measured_extra_wait_min) != null))
    cmp.push({ name: "Attente (min)", prévu: num(toObj.predicted_extra_wait_min), réel: num(toObj.measured_extra_wait_min) });

  // B — Options réelles (plans de mission persistés)
  const planRows = plans ? [
    ["fastest", plans.fastest], ["robust", plans.robust],
    ["efficient", plans.efficient], ["fallback", plans.fallback],
  ] : [];

  return (
    <Panel>
      <div style={{ marginBottom: 4 }}>
        <div className="xray-id" data-testid="xray-decision-id">{d.decision_id}</div>
        <div className="xray-tags">
          <span className={"tag " + (d.is_shadow ? "shadow" : "real")}>{d.is_shadow ? "SHADOW" : "RÉEL"}</span>
          {d.qualification && <span className="tag">{d.qualification}</span>}
          {d.cohort && <span className="tag">{d.cohort}</span>}
          {d.strate && <span className="tag">strate {d.strate}</span>}
          {d.experiment_id && <span className="tag shadow">exp {d.experiment_id}</span>}
        </div>
      </div>

      <Sec idx="A" icon={Layers} title="Contexte — ce que VORTEX savait">
        <KV rows={[
          ["Instant", (d.at || "").replace("T", " ").slice(0, 19)],
          ["Requête", d.request_id],
          ["Réf. externe", d.external_ref],
          ["Config", d.config_version != null ? `v${d.config_version} · ${(d.config_hash || "").slice(0, 10)}` : null],
          ["Solveur", d.solver_version],
          ["Règles", d.ruleset_version],
          ["Mesure", d.measurement_mode],
        ]} />
        {Array.isArray(d.exogenous) && d.exogenous.length > 0 && (
          <div style={{ marginTop: 8, fontSize: 11, color: "#aab8c7" }}>
            Exogène : {d.exogenous.length} facteur(s) enregistré(s).
          </div>
        )}
      </Sec>

      <Sec idx="B" icon={GitCompare} title="Options — plans réellement disponibles">
        {planRows.length === 0 && <div className="nd-inline">{NR} — aucun plan de mission associé.</div>}
        {planRows.map(([key, plan]) => {
          const nr = isNR(plan);
          const { km, min } = planMetrics(plan);
          return (
            <div key={key} className={"plan-card" + (key === selPlanKey ? " sel" : "")} data-testid={`xray-plan-${key}`}>
              <div className="plan-card__t">
                <span>{key.toUpperCase()}{key === selPlanKey ? " · choisi" : ""}</span>
              </div>
              <div className="plan-card__m">
                {nr ? <span className="nd-inline">{NR}</span> :
                  <>{fmt(km, " km") || "km ?"} · {fmt(min, " min") || "durée ?"}</>}
              </div>
            </div>
          );
        })}
      </Sec>

      <Sec idx="C" icon={Target} title="Décision retenue">
        <KV rows={[
          ["Assignation", d.assignment?.resource_id || d.final_decision?.resource_id],
          ["Mission", d.final_decision?.mission_id || d.assignment?.mission_id],
          ["Type de plan", d.final_decision?.plan_type || mission?.plan_type],
          ["Regroupement", d.grouping ? (d.grouping.fused ? "fusion" : (d.grouping.kind || "oui")) : null],
          ["ETA p50", (detail.decision?.eta?.p50 || "").replace("T", " ").slice(0, 19)],
          ["ETA p80", (detail.decision?.eta?.p80 || "").replace("T", " ").slice(0, 19)],
        ]} />
      </Sec>

      <Sec idx="D" icon={HelpCircle} title="Pourquoi — critères enregistrés">
        <KV rows={[
          ["Qualification", d.qualification],
          ["Strate", d.strate],
          ["Cohorte", d.cohort],
          ["Mode mesure", d.measurement_mode],
          ["Shadow", String(d.is_shadow)],
          ["Événements appliqués", Array.isArray(d.applied_events) ? d.applied_events.length : null],
        ]} />
        <div style={{ fontSize: 10.5, color: "#778698", marginTop: 6 }}>
          Critères dérivés uniquement des champs persistés — aucune inférence marketing.
        </div>
      </Sec>

      <Sec idx="E" icon={TrendingUp} title="Prévu vs Réel">
        {cmp.length === 0 ? <div className="nd-inline" data-testid="xray-cmp-nr">{NR}</div> : (
          <div style={{ height: 170 }} data-testid="xray-cmp-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={cmp} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fill: chartTheme.axis, fontSize: 10 }} axisLine={{ stroke: chartTheme.grid }} tickLine={false} />
                <YAxis tick={{ fill: chartTheme.axis, fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#0b1017", border: "1px solid #26374a", borderRadius: 8, fontSize: 12 }} cursor={{ fill: "rgba(34,211,238,0.06)" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="prévu" fill="#5b9dff" radius={[3, 3, 0, 0]} />
                <Bar dataKey="réel" fill="#22d3ee" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Sec>

      <Sec idx="F" icon={TrendingUp} title="Impact mesuré">
        {!toObj ? <div className="nd-inline" data-testid="xray-impact-nr">DATA UNAVAILABLE — outcome non mesuré.</div> : (
          <div className="impact-grid" data-testid="xray-impact">
            <Cell2 label="Km économisés (est.)" value={toObj.estimated_realized_km_saving} unit=" km" good />
            <Cell2 label="Retour à vide" value={toObj.empty_return_km_actual} unit=" km" />
            <Cell2 label="Km retour épargnés" value={toObj.return_km_saved_actual} unit=" km" good />
            <Cell2 label="Route fusionnée vs solo" value={num(toObj.counterfactual_solo_route_km) != null && num(toObj.measured_fused_route_km) != null ? Math.round((toObj.counterfactual_solo_route_km - toObj.measured_fused_route_km) * 100) / 100 : null} unit=" km" good />
          </div>
        )}
      </Sec>

      <Sec idx="G" icon={GitBranch} title="Filiation décisionnelle">
        <div className="lineage" data-testid="xray-lineage">
          <button disabled={!lin.previous} onClick={() => lin.previous && onPickDecision(lin.previous)} data-testid="lineage-prev">
            {lin.previous ? `◀ ${lin.previous.slice(-8)}` : "◀ origine"}
          </button>
          <span className="arw">→</span>
          <button className="cur" disabled>{(lin.current || "").slice(-8) || "actuelle"}</button>
          <span className="arw">→</span>
          <button disabled={!lin.next} onClick={() => lin.next && onPickDecision(lin.next)} data-testid="lineage-next">
            {lin.next ? `${lin.next.slice(-8)} ▶` : "suivante ▶"}
          </button>
        </div>
      </Sec>

      <div className="dj-zone" data-testid="xray-champion-challenger">
        <div className="dj-zone__h"><GitCompare size={12} />Champion vs Challenger — « Qu&apos;aurait fait le challenger ? »</div>
        {!challengerDiff || challengerDiff.found === false ? (
          <div className="diff-ne">CHALLENGER : chargement…</div>
        ) : !challengerDiff.evaluated ? (
          <div className="diff-ne" data-testid="diff-not-evaluated">NOT EVALUATED — aucun challenger n&apos;a évalué cette décision.</div>
        ) : (
          <div className="diff-grid" data-testid="diff-grid">
            <DiffSide side="champ" title="Champion (réel)" v={challengerDiff.champion} />
            <DiffSide side="chal" title="Challenger (shadow)" v={challengerDiff.challenger} onOpen={onPickDecision} />
          </div>
        )}
        {challengerDiff?.experiment_id && (
          <div className="mono" style={{ fontSize: 10, color: "#a78bfa", marginTop: 6 }}>
            expérimentation liée : {String(challengerDiff.experiment_id).slice(-8)} — réglages via l&apos;onglet Laboratoire.
          </div>
        )}
      </div>

      <button className="raw-toggle" onClick={() => setRaw((v) => !v)} data-testid="btn-raw-evidence">
        <FileCode2 size={11} style={{ verticalAlign: "-1px", marginRight: 4 }} />
        {raw ? "Masquer" : "RAW EVIDENCE"} (audit)
      </button>
      {raw && <pre className="raw-pre" data-testid="xray-raw">{JSON.stringify(detail, null, 2)}</pre>}
    </Panel>
  );
}

const Cell2 = ({ label, value, unit, good }) => (
  <div className="impact-cell">
    <div className={"v" + (num(value) != null && value < 0 ? " neg" : "") + (good ? "" : "")}>
      {num(value) == null ? <span className="nd-inline">N/D</span> : `${value}${unit || ""}`}
    </div>
    <div className="l">{label}</div>
  </div>
);

const DR = ({ k, v }) => (
  <div style={{ display: "contents" }}><dt>{k}</dt><dd>{v == null || v === "" ? <span className="nd-inline">N/D</span> : String(v)}</dd></div>
);

const DiffSide = ({ side, title, v, onOpen }) => {
  if (v === "NOT EVALUATED" || !v) return (
    <div className={"diff-side " + side}><h4>{title}</h4><div className="diff-ne">NOT EVALUATED</div></div>
  );
  return (
    <div className={"diff-side " + side}>
      <h4>{title}</h4>
      <dl className="kv" style={{ gridTemplateColumns: "72px 1fr" }}>
        <DR k="Véhicule" v={v.resource_id} />
        <DR k="Plan" v={v.plan_type} />
        <DR k="Km" v={v.planned_km} />
        <DR k="Durée" v={v.planned_min != null ? v.planned_min + " min" : null} />
        <DR k="Score" v={v.score} />
        <DR k="Fusion" v={String(v.grouping_fused)} />
        <DR k="Qualif." v={v.qualification} />
      </dl>
      {side === "chal" && v.decision_id && onOpen && (
        <button className="link-btn" style={{ marginTop: 6 }} onClick={() => onOpen(v.decision_id)} data-testid="diff-open-challenger">
          ouvrir la décision shadow →
        </button>
      )}
    </div>
  );
};

const Panel = ({ children }) => (
  <section className="ck-panel" data-testid="ck-xray">
    <div className="ck-panel__head">
      <h2>Decision X-Ray</h2>
      <span className="sub">preuve réelle · lecture seule</span>
    </div>
    <div className="ck-panel__body">{children}</div>
  </section>
);
